import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../utils/api';
import ChangePasswordModal from '../../../Components/ChangePasswordModal';

const MentorProfile = () => {
  const { authUser } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const data = await api.getGlobalProfile();
        setProfileData(data);
      } catch (error) {
        console.error('Error fetching mentor profile:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
  }, []);

  // Initials for avatar
  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center py-5'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className='card card-body border-0 shadow-sm rounded-4'>
      <div className='d-flex align-items-center justify-content-between gap-3 mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <div
            className='bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3'
            style={{ width: '40px', height: '40px' }}
          >
            <i className='bi bi-person-badge-fill text-primary fs-5'></i>
          </div>
          <div>
            <h1 className='h5 fw-bold mb-0 text-dark'>Profil Mentor</h1>
            <p className='text-muted mb-0 d-none d-lg-block' style={{ fontSize: '0.75rem' }}>
              Informasi detail akun Mentor Anda.
            </p>
          </div>
        </div>
        <button
          type='button'
          className='btn btn-outline-primary btn-sm rounded-pill px-3 fw-bold flex-shrink-0'
          onClick={() => setShowChangePassword(true)}
        >
          <i className='bi bi-shield-lock me-1'></i>
          Ubah Password
        </button>
      </div>

      <ChangePasswordModal
        show={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />

      <div className='d-flex align-items-center gap-3 mb-4'>
        <div
          className='rounded-circle shadow-sm d-flex align-items-center justify-content-center fw-bold text-white bg-primary flex-shrink-0'
          style={{ width: '70px', height: '70px', fontSize: '1.5rem' }}
        >
          {getInitials(profileData?.firstName, profileData?.lastName)}
        </div>
        <div className='flex-grow-1'>
          <div className='d-flex align-items-center gap-2 mb-1'>
            <h2 className='h5 fw-bold mb-0 text-dark'>{profileData?.fullName}</h2>
            <span className='badge bg-dark text-white rounded-pill px-2 py-1 fw-medium' style={{ fontSize: '0.65rem' }}>
              {authUser?.activeSession?.roleName || 'Mentor'}
            </span>
          </div>
          <div className='d-flex align-items-center gap-3 text-muted' style={{ fontSize: '0.8rem' }}>
            <span>
              <i className='bi bi-envelope me-1 text-primary'></i>
              {profileData?.email}
            </span>
            <span>
              <i className='bi bi-telephone-fill me-1 text-primary'></i>
              {profileData?.phoneNumber || '-'}
            </span>
          </div>
        </div>
      </div>

      <div className='row g-4 mb-4'>
        <div className='col-12'>
          <label className='small text-muted mb-3 d-block text-uppercase fw-bold' style={{ fontSize: '0.65rem', letterSpacing: '0.05rem' }}>
            Detail Akun
          </label>
        </div>

        <div className='col-md-6'>
          <label className='small text-muted mb-2 d-block text-uppercase fw-bold' style={{ fontSize: '0.65rem', letterSpacing: '0.05rem' }}>Nama Depan</label>
          <div className='fw-bold text-dark border-bottom pb-2'>{profileData?.firstName || '-'}</div>
        </div>
        <div className='col-md-6'>
          <label className='small text-muted mb-2 d-block text-uppercase fw-bold' style={{ fontSize: '0.65rem', letterSpacing: '0.05rem' }}>Nama Belakang</label>
          <div className='fw-bold text-dark border-bottom pb-2'>{profileData?.lastName || '-'}</div>
        </div>
        <div className='col-md-6 mt-4'>
          <label className='small text-muted mb-2 d-block text-uppercase fw-bold' style={{ fontSize: '0.65rem', letterSpacing: '0.05rem' }}>Nomor KTP</label>
          <div className='fw-bold text-dark border-bottom pb-2'>{profileData?.numberCardId || '-'}</div>
        </div>
        <div className='col-md-6 mt-4'>
          <label className='small text-muted mb-2 d-block text-uppercase fw-bold' style={{ fontSize: '0.65rem', letterSpacing: '0.05rem' }}>Tanggal Lahir</label>
          <div className='fw-bold text-dark border-bottom pb-2'>{profileData?.birthDate || '-'}</div>
        </div>
      </div>

      <div className='row g-4 mb-4'>
        <div className='col-12'>
          <label className='small text-muted mb-3 d-block text-uppercase fw-bold' style={{ fontSize: '0.65rem', letterSpacing: '0.05rem' }}>
            Sosial Media & Info Bank
          </label>
        </div>

        <div className='col-md-4'>
          <label className='small text-muted mb-2 d-block text-uppercase fw-bold' style={{ fontSize: '0.65rem', letterSpacing: '0.05rem' }}>Instagram</label>
          <div className='fw-bold text-dark border-bottom pb-2'>{profileData?.socmedInstagram || '-'}</div>
        </div>
        <div className='col-md-4'>
          <label className='small text-muted mb-2 d-block text-uppercase fw-bold' style={{ fontSize: '0.65rem', letterSpacing: '0.05rem' }}>LinkedIn</label>
          <div className='fw-bold text-dark border-bottom pb-2'>{profileData?.socmedLinkedIn || '-'}</div>
        </div>
        <div className='col-md-4'>
          <label className='small text-muted mb-2 d-block text-uppercase fw-bold' style={{ fontSize: '0.65rem', letterSpacing: '0.05rem' }}>Nama Bank</label>
          <div className='fw-bold text-dark border-bottom pb-2'>{profileData?.bankName || '-'} ({profileData?.bankAccountNumber || '-'})</div>
        </div>
      </div>

      <div className='row g-3 mb-4'>
        <div className='col-12'>
          <label className='small text-muted mb-2 d-block text-uppercase fw-bold' style={{ fontSize: '0.65rem', letterSpacing: '0.05rem' }}>
            Akses Sistem & Peran
          </label>
        </div>

        <div className='col-md-5'>
          <div className='p-3 rounded-4 border border-primary bg-primary bg-opacity-10 h-100 d-flex flex-column justify-content-center'>
            <div className='d-flex align-items-center gap-2 mb-2'>
              <i className='bi bi-person-bounding-box text-primary' style={{ fontSize: '0.85rem' }}></i>
              <span className='fw-bold text-primary' style={{ fontSize: '0.75rem', letterSpacing: '0.02rem' }}>PERAN AKTIF</span>
            </div>
            {authUser?.activeSession ? (
              <div>
                <div className='fw-bold text-dark' style={{ fontSize: '0.9rem' }}>{authUser.activeSession.roleName || authUser.activeSession.roleCode}</div>
                <div className='text-muted mb-2' style={{ fontSize: '0.75rem' }}>{authUser.activeSession.programName || '-'}</div>
                <span className='badge bg-primary text-white rounded-pill px-2 py-1 fw-medium' style={{ fontSize: '0.65rem' }}>
                  Batch {authUser.activeSession.batchName || '-'}
                </span>
              </div>
            ) : (
              <span className='text-muted small'>Tidak ada sesi aktif</span>
            )}
          </div>
        </div>

        <div className='col-md-7'>
          <div className='p-3 rounded-4 border border-light-subtle bg-light h-100'>
            <div className='d-flex align-items-center gap-2 mb-2'>
              <i className='bi bi-list-check text-secondary' style={{ fontSize: '0.85rem' }}></i>
              <span className='fw-bold text-secondary' style={{ fontSize: '0.75rem', letterSpacing: '0.02rem' }}>DAFTAR AKSES</span>
            </div>
            <div className='d-flex flex-column gap-2'>
              {authUser?.availableSessions?.length > 0 ? (
                authUser.availableSessions.map((session, index) => (
                  <div key={index} className='d-flex justify-content-between align-items-center px-3 py-2 rounded-3 border bg-white shadow-sm'>
                    <div>
                      <div className='fw-semibold text-dark' style={{ fontSize: '0.8rem' }}>{session.roleName || session.roleCode}</div>
                      <div className='text-muted' style={{ fontSize: '0.7rem' }}>
                        {session.programName || '-'} • {session.batchName || '-'}
                      </div>
                    </div>
                    {authUser?.activeSession?.roleCode === session.roleCode &&
                     authUser?.activeSession?.batchId === session.batchId && (
                      <i className='bi bi-check-circle-fill text-success' style={{ fontSize: '1rem' }}></i>
                    )}
                  </div>
                ))
              ) : (
                <span className='text-muted small'>Tidak ada akses lain</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorProfile;
